import { useEffect, useState } from "react";
import { api, ApiFailure, ERROR_COPY, type SwitchInfo, type Switches } from "./api";
import { Centered, Header, Shell } from "./components/Shell";
import { Toast } from "./components/Toast";
import { GAME_META, HUB_GROUPS, type GameId } from "./route";
import { haptic } from "./telegram";

function addDaysIso(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function caption(info: SwitchInfo, today: string): string {
  if (info.state === "on") return "у списку";
  if (info.state === "back") {
    return info.back === addDaysIso(today, 1) ? "повернеться завтра" : `повернеться ${info.back?.slice(5) ?? ""}`;
  }
  return info.since === today ? "вимкнена сьогодні" : `вимкнена з ${info.since?.slice(5) ?? ""}`;
}

/**
 * ⚙️ Game switches (admin only — the server checks the Telegram id). Off hides
 * a game now; on brings it back from the next day. `onClose` hands the hub the
 * ids that are off today, so the list updates before its own reload lands.
 */
export function Admin({ onClose }: { onClose: (offToday: string[] | null) => void }) {
  const [data, setData] = useState<Switches | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setData(await api.switches());
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "server_error";
      setError(ERROR_COPY[code] ?? ERROR_COPY.server_error ?? "");
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const flip = async (id: GameId, on: boolean) => {
    if (pending) return;
    setPending(id);
    haptic.tap();
    try {
      setData(await api.setSwitch(id, on));
    } catch (e) {
      haptic.error();
      const code = e instanceof ApiFailure ? e.code : "server_error";
      setToast(ERROR_COPY[code] ?? ERROR_COPY.server_error ?? "");
      window.setTimeout(() => setToast(null), 2200);
    } finally {
      setPending(null);
    }
  };

  const offToday = data ? Object.keys(data.games).filter((id) => data.games[id]?.state !== "on") : null;
  const total = data ? Object.keys(data.games).length : 0;
  const close = () => onClose(offToday);

  const header = (
    <Header
      left={data?.date ?? ""}
      title="АДМІН"
      right={
        <button type="button" onClick={close} className="rounded-lg bg-[rgba(128,128,128,0.15)] px-2 py-1 text-sm active:opacity-70">
          ✕ назад
        </button>
      }
    />
  );

  if (error) {
    return (
      <Shell header={header}>
        <Centered>
          <div>{error}</div>
          <button type="button" onClick={() => void load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
            ще раз
          </button>
        </Centered>
      </Shell>
    );
  }

  return (
    <Shell header={header}>
      <div className="px-4 pb-2 text-xs leading-relaxed text-tghint">
        вимкнена гра одразу зникає з хабу і з постів чіпса. увімкнена повертається з наступного дня. стріки, поки гра на
        паузі, не згорають.
        {offToday && (
          <div className="mt-1 font-semibold text-tgtext">
            у списку сьогодні: {total - offToday.length} з {total}
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+12px)]">
        {HUB_GROUPS.map((group) => (
          <section key={group.title}>
            <div className="sticky top-0 z-10 bg-tgbg pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-tghint">
              {group.title}
            </div>
            <div className="space-y-2">
              {group.games.map((id) => {
                const meta = GAME_META[id];
                const info = data?.games[id];
                // The switch shows intent: a game coming back tomorrow is already "on".
                const wanted = info ? info.state !== "off" : null;
                const busy = pending === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="switch"
                    aria-checked={wanted ?? false}
                    disabled={!info || pending !== null}
                    onClick={() => wanted !== null && void flip(id, !wanted)}
                    className="flex w-full items-center gap-3 rounded-2xl bg-tgsection px-4 py-3 text-left active:opacity-80 disabled:active:opacity-100"
                  >
                    <div className={`text-2xl ${wanted === false ? "opacity-40 grayscale" : ""}`}>{meta.emoji}</div>
                    <div className="min-w-0 flex-1">
                      <div className={`truncate font-bold tracking-wide ${wanted === false ? "text-tghint" : ""}`}>{meta.title}</div>
                      <div className={`truncate text-xs ${info?.state === "back" ? "text-ok" : "text-tghint"}`}>
                        {info && data ? caption(info, data.date) : "…"}
                      </div>
                    </div>
                    <span
                      aria-hidden
                      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
                        wanted ? "bg-tgbutton" : "bg-[rgba(128,128,128,0.35)]"
                      } ${busy ? "animate-pulse" : ""}`}
                    >
                      <span
                        className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-[left] ${
                          wanted ? "left-[1.375rem]" : "left-0.5"
                        }`}
                      />
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      <Toast message={toast} />
    </Shell>
  );
}
